/* theme.ts — Phase B2 (shared theme module).
 *
 * Single source of truth for the app-wide light/dark preference. Mirrors
 * lib/lang.ts in shape and event model so consumers can subscribe to the
 * same patterns:
 *   - storage events  (cross-tab — user toggles in tab A, tab B updates)
 *   - a custom `forecast-theme-change` window event dispatched same-tab
 *     whenever setTheme runs (so listeners re-render without a reload)
 *
 * Before Phase B2 the theme lived in ForecastItaly's local React state
 * with a useEffect applying it to the DOM. ForecastItaly was the ONLY
 * theme writer, so the topbar / sidebar couldn't expose a toggle without
 * duplicating that state. This module moves the writer/reader behind a
 * shared API:
 *
 *   getTheme()       — read current preference (SSR-safe)
 *   setTheme(theme)  — persist + apply + broadcast
 *   applyTheme(t)    — DOM side-effect only (data-theme + body class)
 *   useTheme()       — React hook returning [theme, setTheme]
 *
 * First-load resolution (unchanged from ThemeToggle.tsx's original
 * getInitialTheme): localStorage wins; else prefers-color-scheme; else
 * "light". Defaults match Marco's brief — light is the canonical mode.
 */
import React from "react";

export type Theme = "light" | "dark";

const STORAGE_KEY = "forecast_theme";
const EVENT_NAME = "forecast-theme-change";

/** First-load resolution — read persisted value, else prefers-color-scheme,
 *  else "light". Safe in SSR. */
export function getTheme(): Theme {
  if (typeof window === "undefined") return "light";
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark") return stored;
  } catch {}
  try {
    if (typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-color-scheme: dark)").matches) {
      return "dark";
    }
  } catch {}
  return "light";
}

/** Apply theme to the document. Dual-set during Phase A→E migration:
 *  legacy `body.theme--dark` (used by /tokens.css + inline CSS) plus the
 *  v2 `html[data-theme]` (used by src/styles/theme.css). Phase E drops
 *  the body class once all legacy CSS migrates. */
export function applyTheme(theme: Theme): void {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-theme", theme);
  document.body.classList.toggle("theme--dark", theme === "dark");
}

/** Set theme: persist to localStorage, apply to DOM, broadcast a custom
 *  event so any subscribed component re-renders. */
export function setTheme(theme: Theme): void {
  if (typeof window === "undefined") return;
  applyTheme(theme);
  try { localStorage.setItem(STORAGE_KEY, theme); } catch {}
  try { window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: theme })); } catch {}
}

/** React hook — returns [theme, setTheme]. Re-renders on theme change
 *  (same-tab via the custom event, cross-tab via storage event). The
 *  setter signature matches React's useState setter so call sites can
 *  pattern-match the original ForecastItaly idiom. */
export function useTheme(): [Theme, (theme: Theme) => void] {
  const [theme, setLocal] = React.useState<Theme>(() => getTheme());

  React.useEffect(() => {
    const onCustom = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail === "light" || detail === "dark") setLocal(detail);
      else setLocal(getTheme()); // re-read on payload-less events
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setLocal(getTheme());
    };
    window.addEventListener(EVENT_NAME, onCustom as EventListener);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(EVENT_NAME, onCustom as EventListener);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  // First mount: ensure the DOM matches the resolved initial theme (e.g.
  // if the page loaded with no script applying theme yet, the document
  // could be styled with default tokens until this runs).
  React.useEffect(() => {
    applyTheme(theme);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [theme, setTheme];
}
